import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { AlertTriangle } from "lucide-react";

import { HubDataTable, type HubDataTableColumn } from "@/components/hub/HubDataTable";
import { QuickBooksTxnLink } from "@/components/hub/QuickBooksTxnLink";
import { Card, CardBody } from "@/components/ui/Card";
import { Input, Label } from "@/components/ui/Input";
import { PageTitleWithHelp } from "@/components/ui/title-help";
import type { SortDirection } from "@/components/ui/SortableTableHeader";
import { buildInsideSalesSalesOrderEditPath } from "@/lib/hubEntityDetailPaths";
import { cn } from "@/lib/utils";
import {
  fetchAccountsReceivableUnpaidInvoices,
  fetchAccountsReceivableUnshippedOrders,
  type QboUnpaidInvoiceRow,
  type UnshippedSalesOrderRow,
} from "./accountsReceivableApi";

const PAGE_TABS = [
  { id: "unpaid-invoices", label: "Unpaid invoices" },
  { id: "unshipped-orders", label: "Unshipped orders" },
] as const;

type PageTabId = (typeof PAGE_TABS)[number]["id"];

const HELP_TEXT = [
  "Accounts receivable working views for collections and fulfillment follow-up.",
  "",
  "Unpaid invoices lists QuickBooks Online customer invoices with an open balance (read-only).",
  "Reconnect QuickBooks from Accounting settings when the hub cannot reach QBO.",
  "",
  "Unshipped orders lists sales orders with quantity still to ship in the hub (including complimentary $0 lines).",
  "Remaining quantity uses batch shipped totals; dollar value is remaining quantity times unit price.",
].join("\n");

function formatMoney(amount: number): string {
  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number.isFinite(amount) ? amount : 0);
}

function formatDate(iso: string | null): string {
  if (iso == null || iso.trim() === "") {
    return "—";
  }
  const parsed = new Date(`${iso.includes("T") ? iso.slice(0, 10) : iso}T12:00:00`);
  if (Number.isNaN(parsed.getTime())) {
    return iso;
  }
  return parsed.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function parseActiveTab(searchParams: URLSearchParams): PageTabId {
  const raw = searchParams.get("tab");
  if (raw === "unpaid-invoices" || raw === "unshipped-orders") {
    return raw;
  }
  return PAGE_TABS[0].id;
}

type UnpaidSortKey = "due_date" | "doc_number" | "customer_name" | "txn_date" | "open_balance" | "total_amount" | "qbo";
type UnshippedSortKey =
  | "ship_by_date"
  | "order_number"
  | "customer_name"
  | "end_user_name"
  | "date_approved"
  | "unshipped_value";

const UNPAID_INVOICE_COLUMNS: HubDataTableColumn<QboUnpaidInvoiceRow, UnpaidSortKey>[] = [
  {
    id: "due_date",
    header: "Due",
    sortKey: "due_date",
    overflow: "nowrap",
    sortValue: (row) => row.due_date ?? "",
    cell: (row) => formatDate(row.due_date),
  },
  {
    id: "doc_number",
    header: "Invoice",
    sortKey: "doc_number",
    sortValue: (row) => row.doc_number,
    cell: (row) => row.doc_number,
    cellClassName: "font-medium text-slate-900",
  },
  {
    id: "qbo",
    header: "QBO",
    sortKey: "qbo",
    sortValue: () => "",
    cell: (row) => (
      <QuickBooksTxnLink txnId={row.qb_invoice_id} kind="invoice" documentLabel={row.doc_number} />
    ),
  },
  {
    id: "customer_name",
    header: "Customer",
    sortKey: "customer_name",
    sortValue: (row) => row.customer_name ?? "",
    cell: (row) => row.customer_name ?? "—",
  },
  {
    id: "txn_date",
    header: "Invoice date",
    sortKey: "txn_date",
    overflow: "nowrap",
    sortValue: (row) => row.txn_date,
    cell: (row) => formatDate(row.txn_date),
  },
  {
    id: "total_amount",
    header: "Total",
    sortKey: "total_amount",
    align: "right",
    overflow: "nowrap",
    sortValue: (row) => row.total_amount,
    cellClassName: "text-slate-600",
    cell: (row) => formatMoney(row.total_amount),
  },
  {
    id: "open_balance",
    header: "Open balance",
    sortKey: "open_balance",
    align: "right",
    overflow: "nowrap",
    sortValue: (row) => row.open_balance,
    cellClassName: "font-medium",
    cell: (row) => formatMoney(row.open_balance),
  },
];

const UNSHIPPED_ORDER_COLUMNS: HubDataTableColumn<UnshippedSalesOrderRow, UnshippedSortKey>[] = [
  {
    id: "order_number",
    header: "Order",
    sortKey: "order_number",
    sortValue: (row) => row.order_number,
    cell: (row) => (
      <Link
        to={buildInsideSalesSalesOrderEditPath(row.order_number)}
        className="font-medium text-blue-700 hover:text-blue-900"
      >
        {row.order_number}
      </Link>
    ),
  },
  {
    id: "customer_name",
    header: "Customer",
    sortKey: "customer_name",
    sortValue: (row) => row.customer_name ?? "",
    cell: (row) => row.customer_name ?? "—",
  },
  {
    id: "end_user_name",
    header: "End user",
    sortKey: "end_user_name",
    sortValue: (row) => row.end_user_name ?? "",
    cell: (row) => row.end_user_name ?? "—",
  },
  {
    id: "date_approved",
    header: "Date approved",
    sortKey: "date_approved",
    overflow: "nowrap",
    sortValue: (row) => row.date_approved ?? "",
    cell: (row) => formatDate(row.date_approved),
  },
  {
    id: "ship_by_date",
    header: "Ship-by",
    sortKey: "ship_by_date",
    overflow: "nowrap",
    sortValue: (row) => row.ship_by_date ?? "",
    cell: (row) => formatDate(row.ship_by_date),
  },
  {
    id: "unshipped_value",
    header: "Unshipped $",
    sortKey: "unshipped_value",
    align: "right",
    overflow: "nowrap",
    sortValue: (row) => row.unshipped_value,
    cellClassName: "font-medium",
    cell: (row) => formatMoney(row.unshipped_value),
  },
];

function UnpaidInvoicesPanel(): JSX.Element {
  const query = useQuery({
    queryKey: ["accounting", "accounts-receivable", "unpaid-invoices"],
    queryFn: fetchAccountsReceivableUnpaidInvoices,
    staleTime: 60_000,
  });

  const [search, setSearch] = useState("");
  const [sortKey, setSortKey] = useState<UnpaidSortKey>("due_date");
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");

  const filteredRows = useMemo(() => {
    const needle = search.trim().toLowerCase();
    const rows = query.data?.rows ?? [];
    if (needle === "") {
      return rows;
    }
    return rows.filter((row) => {
      const haystack = [row.doc_number, row.customer_name ?? ""].join(" ").toLowerCase();
      return haystack.includes(needle);
    });
  }, [query.data?.rows, search]);

  const filteredOpenBalance = useMemo(
    () => filteredRows.reduce((sum, row) => sum + row.open_balance, 0),
    [filteredRows],
  );

  function onSort(nextKey: UnpaidSortKey): void {
    if (nextKey === sortKey) {
      setSortDirection((current) => (current === "asc" ? "desc" : "asc"));
      return;
    }
    setSortKey(nextKey);
    setSortDirection(nextKey === "open_balance" ? "desc" : "asc");
  }

  const errorMessage =
    query.isError && query.error instanceof Error ? query.error.message : "Could not load unpaid invoices.";

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3">
      <Card>
        <CardBody className="grid gap-3 sm:grid-cols-[minmax(0,20rem)_1fr] sm:items-end">
          <div>
            <Label htmlFor="ar-unpaid-search">Search</Label>
            <Input
              id="ar-unpaid-search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Invoice number or customer"
            />
          </div>
          <p className="text-sm text-slate-600 sm:text-right">
            <span className="font-medium text-slate-900">{filteredRows.length}</span> open invoice
            {filteredRows.length === 1 ? "" : "s"}
            {search.trim() !== "" ? " matching filters" : ""}
            {" · "}
            Open balance{" "}
            <span className="font-medium tabular-nums text-slate-900">{formatMoney(filteredOpenBalance)}</span>
          </p>
        </CardBody>
      </Card>

      {query.isError ? (
        <Card>
          <CardBody className="flex items-start gap-2 text-sm text-amber-900">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
            <span>{errorMessage}</span>
          </CardBody>
        </Card>
      ) : (
        <HubDataTable
          columns={UNPAID_INVOICE_COLUMNS}
          rows={filteredRows}
          rowKey={(row) => row.qb_invoice_id}
          sortKey={sortKey}
          sortDir={sortDirection}
          onSort={onSort}
          loading={query.isLoading}
          loadingMessage="Loading unpaid QuickBooks invoices…"
          empty="No unpaid invoices in QuickBooks."
          isFiltered={search.trim() !== ""}
          filteredEmpty="No rows match these filters."
          tableClassName="min-w-[52rem]"
        />
      )}
    </div>
  );
}

function UnshippedOrdersPanel(): JSX.Element {
  const query = useQuery({
    queryKey: ["accounting", "accounts-receivable", "unshipped-orders"],
    queryFn: fetchAccountsReceivableUnshippedOrders,
    staleTime: 60_000,
  });

  const [search, setSearch] = useState("");
  const [sortKey, setSortKey] = useState<UnshippedSortKey>("ship_by_date");
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");

  const filteredRows = useMemo(() => {
    const needle = search.trim().toLowerCase();
    const rows = query.data?.rows ?? [];
    if (needle === "") {
      return rows;
    }
    return rows.filter((row) => {
      const haystack = [row.order_number, row.customer_name ?? "", row.end_user_name ?? ""]
        .join(" ")
        .toLowerCase();
      return haystack.includes(needle);
    });
  }, [query.data?.rows, search]);

  const filteredTotal = useMemo(
    () => filteredRows.reduce((sum, row) => sum + row.unshipped_value, 0),
    [filteredRows],
  );

  function onSort(nextKey: UnshippedSortKey): void {
    if (nextKey === sortKey) {
      setSortDirection((current) => (current === "asc" ? "desc" : "asc"));
      return;
    }
    setSortKey(nextKey);
    setSortDirection(nextKey === "unshipped_value" ? "desc" : "asc");
  }

  const errorMessage =
    query.isError && query.error instanceof Error ? query.error.message : "Could not load unshipped orders.";

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3">
      <Card>
        <CardBody className="grid gap-3 sm:grid-cols-[minmax(0,20rem)_1fr] sm:items-end">
          <div>
            <Label htmlFor="ar-unshipped-search">Search</Label>
            <Input
              id="ar-unshipped-search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Order, customer, or end user"
            />
          </div>
          <p className="text-sm text-slate-600 sm:text-right">
            <span className="font-medium text-slate-900">{filteredRows.length}</span> order
            {filteredRows.length === 1 ? "" : "s"}
            {search.trim() !== "" ? " matching filters" : ""}
            {" · "}
            Unshipped value{" "}
            <span className="font-medium tabular-nums text-slate-900">{formatMoney(filteredTotal)}</span>
          </p>
        </CardBody>
      </Card>

      {query.isError ? (
        <Card>
          <CardBody className="text-sm text-amber-900">{errorMessage}</CardBody>
        </Card>
      ) : (
        <HubDataTable
          columns={UNSHIPPED_ORDER_COLUMNS}
          rows={filteredRows}
          rowKey={(row) => row.order_number}
          sortKey={sortKey}
          sortDir={sortDirection}
          onSort={onSort}
          loading={query.isLoading}
          loadingMessage="Loading unshipped sales orders…"
          empty="No unshipped sales orders."
          isFiltered={search.trim() !== ""}
          filteredEmpty="No rows match these filters."
          tableClassName="min-w-[56rem]"
        />
      )}
    </div>
  );
}

export function AccountsReceivablePage(): JSX.Element {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = parseActiveTab(searchParams);

  function selectTab(tabId: PageTabId): void {
    setSearchParams({ tab: tabId }, { replace: true });
  }

  return (
    <div className="flex h-full min-h-0 flex-col gap-4 p-4 md:p-6">
      <PageTitleWithHelp hoverHint={HELP_TEXT}>Accounts receivable</PageTitleWithHelp>

      <div className="flex flex-wrap gap-2 border-b border-slate-200 pb-2">
        {PAGE_TABS.map((tab) => {
          const selected = tab.id === activeTab;
          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => selectTab(tab.id)}
              className={cn(
                "rounded-md px-3 py-1.5 text-sm font-medium transition-colors",
                selected
                  ? "bg-slate-900 text-white"
                  : "text-slate-600 hover:bg-slate-100 hover:text-slate-900",
              )}
            >
              {tab.label}
            </button>
          );
        })}
      </div>

      <div className="flex min-h-0 flex-1 flex-col">
        {activeTab === "unpaid-invoices" ? <UnpaidInvoicesPanel /> : <UnshippedOrdersPanel />}
      </div>
    </div>
  );
}
